import Highcharts from "highcharts";

const CROSSHAIR_STYLES = {
    color: '#605BFF',
    width: 1.5,
    dashArray: '1,6',
    zIndex: 6
} as const;

const SYNC_PROPERTIES = {
    syncId: '__syncId',
    eventHandlers: '__syncEventHandlers',
    customCrosshair: 'customCrosshair'
} as const;

interface ChartWithSync extends Highcharts.Chart {
    [SYNC_PROPERTIES.syncId]?: string;
    [SYNC_PROPERTIES.eventHandlers]?: {
        mouseMoveHandler: (event: MouseEvent) => void;
        mouseLeaveHandler: () => void;
    };
    [SYNC_PROPERTIES.customCrosshair]?: Highcharts.SVGElement;
}

interface TooltipPositionPreference {
    positionType: 'top' | 'bottom' | 'left' | 'right';
    normalizedY: number; // Normalized Y position (0-1) of the point in the source chart
}

interface ChartSyncManager {
    charts: Map<string, Highcharts.Chart>;
    currentHoveredSeries: number | null;
    tooltipPositionPreference: TooltipPositionPreference | null;
    register: (chartId: string, chart: Highcharts.Chart) => void;
    unregister: (chartId: string) => void;
    syncTooltip: (sourceChartId: string, point: Highcharts.Point) => void;
    syncCrosshair: (sourceChartId: string, event: Highcharts.PointerEventObject) => void;
    syncSeriesHighlight: (sourceChartId: string, seriesIndex: number, highlight: boolean) => void;
    syncTooltipAtCategory: (sourceChartId: string, categoryIndex: number, hoveredSeriesIndex?: number) => void;
    syncTooltipAndMarkersAtCategory: (sourceChartId: string, categoryIndex: number, hoveredSeriesIndex?: number) => void;
    setHoveredSeries: (seriesIndex: number | null) => void;
    setTooltipPositionPreference: (preference: TooltipPositionPreference | null) => void;
    clear: () => void;
}

/**
 * Global chart synchronization manager
 * Manages multiple chart instances and provides tooltip/crosshair synchronization
 */
class ChartSyncManagerImpl implements ChartSyncManager {
    public charts: Map<string, Highcharts.Chart> = new Map();
    private isUpdating: boolean = false;
    public currentHoveredSeries: number | null = null;
    public tooltipPositionPreference: TooltipPositionPreference | null = null;

    /**
     * Register a chart for synchronization
     */
    register(chartId: string, chart: Highcharts.Chart): void {
        this.charts.set(chartId, chart);
        this.addSyncEventListeners(chartId, chart);
    }

    /**
     * Unregister a chart from synchronization
     */
    unregister(chartId: string): void {
        const chart = this.charts.get(chartId);
        if (chart) {
            this.removeSyncEventListeners(chart);
            this.charts.delete(chartId);
        }
    }

    /**
     * Synchronize tooltip across all registered charts
     */
    syncTooltip(sourceChartId: string, point: Highcharts.Point): void {
        if (this.isUpdating) return;

        this.isUpdating = true;

        try {
            const sourceChart = this.charts.get(sourceChartId);
            if (!sourceChart) return;

            const categoryIndex = point.index;
            
            // Calculate position preference from source chart
            // We need to estimate tooltip dimensions - use default values if not available
            const estimatedTooltipWidth = 150; // Reasonable default
            const estimatedTooltipHeight = 60; // Reasonable default
            const positionType = this.calculateTooltipPositionPreference(
                sourceChart,
                point,
                estimatedTooltipWidth,
                estimatedTooltipHeight
            );
            const normalizedY = sourceChart.plotHeight > 0 ? point.plotY / sourceChart.plotHeight : 0.5;
            
            this.setTooltipPositionPreference({ positionType, normalizedY });
            
            const targetCharts = this.getTargetCharts(sourceChartId);

            targetCharts.forEach(({ chart, chartId }) => {
                this.syncTooltipForChart(chart, chartId, categoryIndex);
            });
        } finally {
            this.isUpdating = false;
        }
    }

    /**
     * Synchronize crosshair across all registered charts
     */
    syncCrosshair(sourceChartId: string, event: Highcharts.PointerEventObject): void {
        if (this.isUpdating) return;

        this.isUpdating = true;

        try {
            const sourceChart = this.charts.get(sourceChartId);
            if (!sourceChart) return;

            const categoryIndex = this.calculateCategoryIndex(sourceChart, event);
            if (categoryIndex < 0) return;

            // Sync crosshairs to all charts (including source)
            this.charts.forEach((chart, chartId) => {
                this.syncCrosshairForChart(chart, chartId, categoryIndex);
            });

            // Calculate position preference from source chart for alignment
            const pointsToShow = this.getValidPointsAtCategory(sourceChart, categoryIndex);
            if (pointsToShow.length > 0) {
                const sourcePoint = pointsToShow[0];
                const estimatedTooltipWidth = 150;
                const estimatedTooltipHeight = 60;
                const positionType = this.calculateTooltipPositionPreference(
                    sourceChart,
                    sourcePoint,
                    estimatedTooltipWidth,
                    estimatedTooltipHeight
                );
                const normalizedY = sourceChart.plotHeight > 0 ? sourcePoint.plotY / sourceChart.plotHeight : 0.5;
                this.setTooltipPositionPreference({ positionType, normalizedY });
            }

            // Sync tooltips and markers for non-source charts only
            this.getTargetCharts(sourceChartId).forEach(({ chart, chartId }) => {
                this.syncTooltipAndMarkersAtCategory(chartId, categoryIndex, this.currentHoveredSeries);
            });
        } finally {
            this.isUpdating = false;
        }
    }

    /**
     * Synchronize series highlighting across all registered charts
     */
    syncSeriesHighlight(sourceChartId: string, seriesIndex: number, highlight: boolean): void {
        if (this.isUpdating) return;

        this.isUpdating = true;

        try {
            this.getTargetCharts(sourceChartId).forEach(({ chart, chartId }) => {
                this.syncSeriesHighlightForChart(chart, chartId, seriesIndex, highlight);
            });
        } finally {
            this.isUpdating = false;
        }
    }

    /**
     * Synchronize tooltips at a specific category index across all charts
     */
    syncTooltipAtCategory(sourceChartId: string, categoryIndex: number, hoveredSeriesIndex?: number): void {
        if (this.isUpdating) return;

        this.isUpdating = true;

        try {
            const sourceChart = this.charts.get(sourceChartId);
            if (sourceChart) {
                // Calculate position preference from source chart
                const pointsToShow = this.getValidPointsAtCategory(sourceChart, categoryIndex);
                if (pointsToShow.length > 0) {
                    const sourcePoint = pointsToShow[0];
                    const estimatedTooltipWidth = 150;
                    const estimatedTooltipHeight = 60;
                    const positionType = this.calculateTooltipPositionPreference(
                        sourceChart,
                        sourcePoint,
                        estimatedTooltipWidth,
                        estimatedTooltipHeight
                    );
                    const normalizedY = sourceChart.plotHeight > 0 ? sourcePoint.plotY / sourceChart.plotHeight : 0.5;
                    this.setTooltipPositionPreference({ positionType, normalizedY });
                }
            }

            this.getTargetCharts(sourceChartId).forEach(({ chart, chartId }) => {
                this.syncTooltipAtCategoryForChart(chart, chartId, categoryIndex, hoveredSeriesIndex);
            });
        } finally {
            this.isUpdating = false;
        }
    }

    /**
     * Synchronize tooltips and point markers at a specific category index
     */
    syncTooltipAndMarkersAtCategory(chartId: string, categoryIndex: number, hoveredSeriesIndex?: number): void {
        const chart = this.charts.get(chartId);
        if (!chart?.series) return;

        try {
            this.clearPointStates(chart);
            const pointsToShow = this.getValidPointsAtCategory(chart, categoryIndex);

            if (pointsToShow.length > 0) {
                // Note: Position preference should already be set by the caller (syncCrosshair or syncTooltipAtCategory)
                chart.tooltip.refresh(pointsToShow);
                this.highlightHoveredPoint(chart, categoryIndex, hoveredSeriesIndex);
            }
        } catch (error) {
            console.warn(`Error syncing tooltip and markers for chart ${chartId}:`, error);
        }
    }

    /**
     * Set the currently hovered series index
     */
    setHoveredSeries(seriesIndex: number | null): void {
        this.currentHoveredSeries = seriesIndex;
    }

    /**
     * Set the tooltip position preference for synchronized tooltips
     */
    setTooltipPositionPreference(preference: TooltipPositionPreference | null): void {
        this.tooltipPositionPreference = preference;
    }

    /**
     * Clear all registered charts
     */
    clear(): void {
        this.charts.forEach((chart) => {
            this.removeSyncEventListeners(chart);
        });
        this.charts.clear();
        this.tooltipPositionPreference = null;
    }

    // Private helper methods

    private getTargetCharts(sourceChartId: string): Array<{ chart: Highcharts.Chart; chartId: string }> {
        return Array.from(this.charts.entries())
            .filter(([chartId]) => chartId !== sourceChartId)
            .map(([chartId, chart]) => ({ chart, chartId }));
    }

    private calculateCategoryIndex(sourceChart: Highcharts.Chart, event: Highcharts.PointerEventObject): number {
        const sourceXAxis = sourceChart.xAxis[0];
        if (!sourceXAxis || event.chartX === undefined) return -1;

        const relativeX = event.chartX - sourceChart.plotLeft;
        const normalizedX = relativeX / sourceChart.plotWidth;
        const categories = sourceXAxis.categories || [];

        const categoryIndex = Math.round(normalizedX * (categories.length - 1));
        return Math.max(0, Math.min(categoryIndex, categories.length - 1));
    }

    private syncTooltipForChart(chart: Highcharts.Chart, chartId: string, categoryIndex: number): void {
        if (!chart?.renderer) return;

        try {
            const pointsToShow = this.getValidPointsAtCategory(chart, categoryIndex);
            if (pointsToShow.length > 0) {
                chart.tooltip.refresh(pointsToShow);
            }
        } catch (error) {
            console.warn(`Error syncing tooltip for chart ${chartId}:`, error);
        }
    }

    private syncCrosshairForChart(chart: Highcharts.Chart, chartId: string, categoryIndex: number): void {
        if (!chart?.renderer) return;

        try {
            // Remove any existing custom crosshair
            this.removeCustomCrosshair(chart);

            // Hide any built-in crosshairs to prevent conflicts
            this.hideBuiltInCrosshairs(chart);

            const targetXAxis = chart.xAxis[0];
            if (!targetXAxis) return;

            const targetCategories = targetXAxis.categories || [];
            if (categoryIndex >= targetCategories.length) return;

            const targetX = this.calculateCrosshairXPosition(chart, categoryIndex, targetCategories.length);
            this.createCustomCrosshair(chart, targetX);
        } catch (error) {
            console.warn(`Error syncing crosshair for chart ${chartId}:`, error);
        }
    }

    private syncSeriesHighlightForChart(chart: Highcharts.Chart, chartId: string, seriesIndex: number, highlight: boolean): void {
        if (!chart?.series) return;

        try {
            const targetSeries = chart.series[seriesIndex];
            if (targetSeries) {
                targetSeries.setState(highlight ? 'hover' : '');
            }
        } catch (error) {
            console.warn(`Error syncing series highlight for chart ${chartId}:`, error);
        }
    }

    private syncTooltipAtCategoryForChart(chart: Highcharts.Chart, chartId: string, categoryIndex: number, hoveredSeriesIndex?: number): void {
        if (!chart?.renderer) return;

        try {
            this.clearPointStates(chart);
            const pointsToShow = this.getValidPointsAtCategory(chart, categoryIndex);

            if (pointsToShow.length > 0) {
                this.highlightPoints(pointsToShow);
                chart.tooltip.refresh(pointsToShow);
            }
        } catch (error) {
            console.warn(`Error syncing tooltip at category for chart ${chartId}:`, error);
        }
    }

    private getValidPointsAtCategory(chart: Highcharts.Chart, categoryIndex: number): Highcharts.Point[] {
        return chart.series
            .map(series => series.data[categoryIndex])
            .filter(point => point && point.visible !== false && point.y !== null && point.y !== undefined);
    }

    private clearPointStates(chart: Highcharts.Chart): void {
        chart.series.forEach(series => {
            series.data.forEach(point => {
                if (point?.setState) {
                    point.setState('');
                }
            });
        });
    }

    private highlightPoints(points: Highcharts.Point[]): void {
        points.forEach(point => {
            if (point?.setState) {
                point.setState('hover');
            }
        });
    }

    private highlightHoveredPoint(chart: Highcharts.Chart, categoryIndex: number, hoveredSeriesIndex?: number): void {
        if (hoveredSeriesIndex === undefined || hoveredSeriesIndex === null ||
            hoveredSeriesIndex < 0 || hoveredSeriesIndex >= chart.series.length) {
            return;
        }

        const hoveredSeries = chart.series[hoveredSeriesIndex];
        const hoveredPoint = hoveredSeries?.data[categoryIndex];

        if (hoveredPoint?.setState) {
            hoveredPoint.setState('hover');
        }
    }

    private calculateCrosshairXPosition(chart: Highcharts.Chart, categoryIndex: number, totalCategories: number): number {
        const targetNormalizedX = totalCategories > 1 ?
            categoryIndex / (totalCategories - 1) : 0.5;
        return chart.plotLeft + targetNormalizedX * chart.plotWidth;
    }

    private removeCustomCrosshair(chart: ChartWithSync): void {
        if (chart[SYNC_PROPERTIES.customCrosshair]) {
            chart[SYNC_PROPERTIES.customCrosshair]!.destroy();
            delete chart[SYNC_PROPERTIES.customCrosshair];
        }
    }

    private createCustomCrosshair(chart: Highcharts.Chart, targetX: number): void {
        const crosshairPath = [
            ['M', targetX, chart.plotTop],
            ['L', targetX, chart.plotTop + chart.plotHeight]
        ] as any;

        (chart as ChartWithSync)[SYNC_PROPERTIES.customCrosshair] = chart.renderer.path(crosshairPath)
            .attr({
                stroke: CROSSHAIR_STYLES.color,
                'stroke-width': CROSSHAIR_STYLES.width,
                'stroke-dasharray': CROSSHAIR_STYLES.dashArray,
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
                fill: 'none',
                zIndex: CROSSHAIR_STYLES.zIndex
            })
            .add();
    }

    private addSyncEventListeners(chartId: string, chart: Highcharts.Chart): void {
        const chartWithSync = chart as ChartWithSync;
        chartWithSync[SYNC_PROPERTIES.syncId] = chartId;

        const mouseMoveHandler = (event: MouseEvent) => {
            if (!this.isUpdating) {
                const rect = chart.container.getBoundingClientRect();
                const chartEvent = {
                    chartX: event.clientX - rect.left,
                    chartY: event.clientY - rect.top
                } as Highcharts.PointerEventObject;

                this.syncCrosshair(chartId, chartEvent);
            }
        };

        const mouseLeaveHandler = () => {
            if (!this.isUpdating) {
                this.currentHoveredSeries = null;
                this.setTooltipPositionPreference(null);
                this.hideAllTooltips();
                this.hideAllCrosshairs();
            }
        };

        chartWithSync[SYNC_PROPERTIES.eventHandlers] = {
            mouseMoveHandler,
            mouseLeaveHandler
        };

        chart.container.addEventListener('mousemove', mouseMoveHandler);
        chart.container.addEventListener('mouseleave', mouseLeaveHandler);
    }

    private removeSyncEventListeners(chart: Highcharts.Chart): void {
        const chartWithSync = chart as ChartWithSync;

        // Remove event listeners
        const handlers = chartWithSync[SYNC_PROPERTIES.eventHandlers];
        if (handlers) {
            chart.container.removeEventListener('mousemove', handlers.mouseMoveHandler);
            chart.container.removeEventListener('mouseleave', handlers.mouseLeaveHandler);
            delete chartWithSync[SYNC_PROPERTIES.eventHandlers];
        }

        // Clean up custom crosshair
        this.removeCustomCrosshair(chartWithSync);
    }

    private hideAllTooltips(sourceChartId?: string): void {
        this.charts.forEach((chart, chartId) => {
            if ((!sourceChartId || chartId !== sourceChartId) && chart?.tooltip) {
                try {
                    chart.tooltip.hide();
                    this.clearPointStates(chart);
                } catch (error) {
                    console.warn(`Error hiding tooltip for chart ${chartId}:`, error);
                }
            }
        });
    }

    private hideAllCrosshairs(sourceChartId?: string): void {
        this.charts.forEach((chart, chartId) => {
            if ((!sourceChartId || chartId !== sourceChartId) && chart) {
                try {
                    this.removeCustomCrosshair(chart as ChartWithSync);
                    this.hideBuiltInCrosshairs(chart);
                } catch (error) {
                    console.warn(`Error hiding crosshair for chart ${chartId}:`, error);
                }
            }
        });
    }

    private hideBuiltInCrosshairs(chart: Highcharts.Chart): void {
        if (!chart.xAxis) return;

        chart.xAxis.forEach((axis: any) => {
            if (axis.hideCrosshair && (axis.options as any).crosshair) {
                axis.hideCrosshair();
            }
        });
    }

    /**
     * Calculate the preferred tooltip position type for a point
     * This is used to ensure consistent positioning across synced charts
     */
    private calculateTooltipPositionPreference(
        chart: Highcharts.Chart,
        point: Highcharts.Point,
        labelWidth: number,
        labelHeight: number
    ): 'top' | 'bottom' | 'left' | 'right' {
        const MIN_SPACING = 10;
        const POINT_RADIUS = 5;
        
        const pointX = point.plotX + chart.plotLeft;
        const pointY = point.plotY + chart.plotTop;
        const normalizedY = chart.plotHeight > 0 ? point.plotY / chart.plotHeight : 0.5;

        // Calculate positions
        const positions = [
            { x: pointX - labelWidth / 2, y: pointY - labelHeight - MIN_SPACING, name: 'top' as const },
            { x: pointX - labelWidth / 2, y: pointY + POINT_RADIUS + MIN_SPACING, name: 'bottom' as const },
            { x: pointX - labelWidth - MIN_SPACING, y: pointY - labelHeight / 2, name: 'left' as const },
            { x: pointX + POINT_RADIUS + MIN_SPACING, y: pointY - labelHeight / 2, name: 'right' as const }
        ];

        // Score positions
        const scores = positions.map(pos => {
            let score = 0;
            
            // Check bounds
            const inBounds = pos.x >= 0 && pos.y >= 0 && 
                           pos.x + labelWidth <= chart.chartWidth &&
                           pos.y + labelHeight <= chart.chartHeight;
            if (!inBounds) score -= 500;

            // Prefer top/bottom for line charts
            if (pos.name === 'top' || pos.name === 'bottom') {
                score += 10;
            }

            // Prefer positions that work for the point's location
            if (pos.name === 'top' && normalizedY > 0.5) score += 50;
            if (pos.name === 'bottom' && normalizedY < 0.5) score += 50;

            return { ...pos, score };
        });

        // Find best position
        const best = scores.reduce((best, current) => 
            current.score > best.score ? current : best
        );

        return best.name;
    }
}

// Global instance
export const chartSyncManager: ChartSyncManager = new ChartSyncManagerImpl();

/**
 * Hook to register/unregister a chart with the sync manager
 */
export const useChartSync = (chartId: string, chartRef: React.RefObject<any>) => {
    const registerChart = () => {
        if (chartRef.current?.chart && chartId) {
            chartSyncManager.register(chartId, chartRef.current.chart);
        }
    };

    const unregisterChart = () => {
        if (chartId) {
            chartSyncManager.unregister(chartId);
        }
    };

    return { registerChart, unregisterChart };
};

/**
 * Enhanced chart options that include synchronization events
 */
export const createSyncEnabledChartOptions = (
    baseOptions: Highcharts.Options,
    chartId: string
): Highcharts.Options => {
    return {
        ...baseOptions,
        tooltip: {
            ...baseOptions.tooltip,
            positioner: function (this: Highcharts.Tooltip, labelWidth: number, labelHeight: number, point: Highcharts.Point) {
                // Get the chart instance
                const chart = this.chart;
                if (!chart || !point) {
                    return { x: 0, y: 0 };
                }

                const MIN_SPACING = 10; // Minimum space between tooltip and point
                const POINT_RADIUS = 5; // Approximate radius of the point marker

                // Calculate the point's absolute position
                const pointX = point.plotX + chart.plotLeft;
                const pointY = point.plotY + chart.plotTop;

                // Check if there's a position preference from the source chart (for synced tooltips)
                const positionPreference = chartSyncManager.tooltipPositionPreference;
                
                // If we have a position preference, try to use the same positioning strategy
                // but adjust if it doesn't work for this chart
                let preferredPositionType: 'top' | 'bottom' | 'left' | 'right' | null = null;
                
                if (positionPreference) {
                    // Use the preferred position type from source chart
                    preferredPositionType = positionPreference.positionType;
                }

                // Define potential positions (top, bottom, left, right)
                const positions: Array<{ x: number; y: number; name: string }> = [];

                // Try position above the point
                // For horizontal alignment, center tooltip on the point's X position
                const topX = pointX - labelWidth / 2;
                const topY = pointY - labelHeight - MIN_SPACING;
                positions.push({
                    x: topX,
                    y: topY,
                    name: 'top'
                });

                // Try position below the point
                // For horizontal alignment, center tooltip on the point's X position
                const bottomX = pointX - labelWidth / 2;
                const bottomY = pointY + POINT_RADIUS + MIN_SPACING;
                positions.push({
                    x: bottomX,
                    y: bottomY,
                    name: 'bottom'
                });

                // Try position to the left of the point
                const leftX = pointX - labelWidth - MIN_SPACING;
                const leftY = pointY - labelHeight / 2;
                positions.push({
                    x: leftX,
                    y: leftY,
                    name: 'left'
                });

                // Try position to the right of the point
                const rightX = pointX + POINT_RADIUS + MIN_SPACING;
                const rightY = pointY - labelHeight / 2;
                positions.push({
                    x: rightX,
                    y: rightY,
                    name: 'right'
                });

                // Helper function to check if tooltip would cover the point
                const coversPoint = (posX: number, posY: number): boolean => {
                    const tooltipLeft = posX;
                    const tooltipRight = posX + labelWidth;
                    const tooltipTop = posY;
                    const tooltipBottom = posY + labelHeight;

                    const pointLeft = pointX - POINT_RADIUS;
                    const pointRight = pointX + POINT_RADIUS;
                    const pointTop = pointY - POINT_RADIUS;
                    const pointBottom = pointY + POINT_RADIUS;

                    return !(
                        tooltipRight < pointLeft ||
                        tooltipLeft > pointRight ||
                        tooltipBottom < pointTop ||
                        tooltipTop > pointBottom
                    );
                };

                // Helper function to check if position is within chart bounds
                const isWithinBounds = (posX: number, posY: number): boolean => {
                    return posX >= 0 &&
                        posY >= 0 &&
                        posX + labelWidth <= chart.chartWidth &&
                        posY + labelHeight <= chart.chartHeight;
                };

                // Score positions: prefer positions that don't cover point and are within bounds
                const scorePosition = (pos: { x: number; y: number; name: string }): number => {
                    let score = 0;
                    
                    // Major penalty for covering the point
                    if (coversPoint(pos.x, pos.y)) {
                        score -= 1000;
                    }

                    // Penalty for being outside bounds
                    if (!isWithinBounds(pos.x, pos.y)) {
                        score -= 500;
                    }

                    // Bonus for matching preferred position type (for synced alignment)
                    if (preferredPositionType && pos.name === preferredPositionType) {
                        score += 200;
                    }

                    // Preference for top/bottom over left/right (better visual flow)
                    if (pos.name === 'top' || pos.name === 'bottom') {
                        score += 10;
                    }

                    // Preference for positions closer to the point
                    const distanceFromPoint = Math.sqrt(
                        Math.pow(pos.x + labelWidth / 2 - pointX, 2) +
                        Math.pow(pos.y + labelHeight / 2 - pointY, 2)
                    );
                    score -= distanceFromPoint * 0.1;

                    return score;
                };

                // Score all positions and find the best one
                let bestPosition = positions[0];
                let bestScore = scorePosition(positions[0]);

                for (let i = 1; i < positions.length; i++) {
                    const score = scorePosition(positions[i]);
                    if (score > bestScore) {
                        bestScore = score;
                        bestPosition = positions[i];
                    }
                }

                // Clamp the position to ensure it stays within chart bounds
                let finalX = Math.max(0, Math.min(bestPosition.x, chart.chartWidth - labelWidth));
                let finalY = Math.max(0, Math.min(bestPosition.y, chart.chartHeight - labelHeight));

                // If the best position still covers the point, try to adjust it
                if (coversPoint(finalX, finalY)) {
                    // If covering, try to shift away from the point
                    if (bestPosition.name === 'top' || bestPosition.name === 'bottom') {
                        // Shift horizontally if needed
                        if (finalX + labelWidth / 2 < pointX) {
                            // Tooltip is left of point, try moving right
                            finalX = Math.min(finalX + (pointX - (finalX + labelWidth / 2)) + MIN_SPACING, chart.chartWidth - labelWidth);
                        } else if (finalX + labelWidth / 2 > pointX) {
                            // Tooltip is right of point, try moving left
                            finalX = Math.max(finalX - ((finalX + labelWidth / 2) - pointX) - MIN_SPACING, 0);
                        }
                    } else {
                        // Shift vertically if needed
                        if (finalY + labelHeight / 2 < pointY) {
                            // Tooltip is above point, try moving down
                            finalY = Math.min(finalY + (pointY - (finalY + labelHeight / 2)) + MIN_SPACING, chart.chartHeight - labelHeight);
                        } else if (finalY + labelHeight / 2 > pointY) {
                            // Tooltip is below point, try moving up
                            finalY = Math.max(finalY - ((finalY + labelHeight / 2) - pointY) - MIN_SPACING, 0);
                        }
                    }
                }

                return {
                    x: finalX,
                    y: finalY
                };
            }
        },
        plotOptions: {
            ...baseOptions.plotOptions,
            series: {
                ...baseOptions.plotOptions?.series,
                point: {
                    events: {
                        mouseOver: function (this: Highcharts.Point) {
                            // Sync tooltip and crosshair when hovering over a point
                            chartSyncManager.syncTooltip(chartId, this);

                            // Also trigger crosshair sync with the point's position
                            const chart = this.series.chart;
                            const event = {
                                chartX: this.plotX + chart.plotLeft,
                                chartY: this.plotY + chart.plotTop
                            } as Highcharts.PointerEventObject;

                            chartSyncManager.syncCrosshair(chartId, event);
                        }
                    }
                },
                events: {
                    mouseOver: function (this: Highcharts.Series) {
                        // Track the hovered series and sync highlighting
                        const seriesIndex = this.chart.series.indexOf(this);
                        chartSyncManager.setHoveredSeries(seriesIndex);
                        chartSyncManager.syncSeriesHighlight(chartId, seriesIndex, true);
                    },
                    mouseOut: function (this: Highcharts.Series) {
                        // Clear hovered series and remove highlighting
                        const seriesIndex = this.chart.series.indexOf(this);
                        chartSyncManager.setHoveredSeries(null);
                        chartSyncManager.syncSeriesHighlight(chartId, seriesIndex, false);
                    }
                }
            }
        },
        chart: {
            ...baseOptions.chart,
            events: {
                ...baseOptions.chart?.events,
                load: function (this: Highcharts.Chart) {
                    // Register chart when it loads
                    chartSyncManager.register(chartId, this);
                }
            }
        }
    };
};